import { Box, Flex } from '@withjoy/joykit';
import React, { useEffect, useState } from 'react';
import type { QRCodeLayerData } from './Layer.types';
import { textLayerDataToCSS } from './TextLayer';
import { useFont } from '../../../hooks/useFont';
import { useCreateQRCodeMutation } from '../CardCustomizer.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import type { PageEditor } from './Page';
import { layerLayoutToCss } from '@apps/card/utils';
import PLACEHOLDER_QR_IMAGE from '../assets/placeholderQR.png';
import { TEMPLATE_PLACEHOLDER_LINK } from '../CardCustomizer.constants';

export const QRCodeLayer = (props: { editor?: PageEditor; layer: QRCodeLayerData; isActive?: boolean; format?: StationeryDraftFormat }) => {
  const { editor, layer, isActive, format } = props;
  const { link, password, fill, font, text, hideImage } = layer.qrcodeData;

  const isPlaceholderLink = !link || link === TEMPLATE_PLACEHOLDER_LINK;

  const [qrCodeImage, setQrCodeImage] = useState<string | undefined>(undefined);
  const [createQRCode] = useCreateQRCodeMutation();
  const hideQRCodeImage = format === StationeryDraftFormat.digital && hideImage;
  useEffect(() => {
    void (async () => {
      if (!hideQRCodeImage && !isPlaceholderLink) {
        try {
          const url = new URL(link);

          if (url) {
            url.searchParams.set('utm_source', 'qr');
            url.searchParams.set('utm_medium', 'paper');
            url.searchParams.set('utm_campaign', 'stationery_qr');
            url.searchParams.set('src', 'print');

            const { data } = await createQRCode({ variables: { data: { link: url.toString(), fillColor: fill } } });

            if (data) {
              setQrCodeImage(data.createQRCode);
            }
          }
        } catch {
          // Swallow — QR image generation failure falls back to the placeholder.
        }
      }
    })();
  }, [createQRCode, link, hideQRCodeImage, isPlaceholderLink, fill]);

  useFont(`${font.family}:${font.weight}${font.style ?? ''}`);

  const alignItems = (() => {
    if (text.alignHorizontal === 'left') return 'flex-start';
    if (text.alignHorizontal === 'right') return 'flex-end';
    return 'center';
  })();

  const isInEditor = !!editor;
  // Display text below the QR code: strip UTMs so the printed card shows a clean, readable URL.
  // The QR code image itself still encodes the UTM-tagged URL (see `createQRCode` above) so the
  // scanner opens the attributed URL — only the human-readable label is normalized.
  const formattedLink = (() => {
    try {
      const url = new URL(link);
      (['utm_source', 'utm_medium', 'utm_campaign', 'utm_source_secondary'] as const).forEach(key => url.searchParams.delete(key));
      return url.toString().replace(/^https?:\/\//, '');
    } catch {
      return link.replace(/^https?:\/\//, '');
    }
  })();
  const shouldHighlightLink = format === StationeryDraftFormat.digital;

  const showPlaceholderQR = !hideQRCodeImage && isPlaceholderLink;
  const showGeneratedQR = !hideQRCodeImage && !isPlaceholderLink && !!qrCodeImage;

  return (
    <Flex
      className="introable"
      cursor={isActive ? 'move' : 'default'}
      userSelect="none"
      alignItems={alignItems}
      flexDirection="column"
      gap="5px"
      style={{
        ...textLayerDataToCSS(layer.qrcodeData),
        ...layerLayoutToCss(layer.layout),
        height: 'unset',
        wordBreak: 'break-word',
      }}
    >
      {showPlaceholderQR ? (
        // placeholder QR code image
        <Box
          draggable={false}
          width={72}
          height={72}
          style={{
            maskImage: `url(${PLACEHOLDER_QR_IMAGE})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            backgroundColor: fill,
          }}
        />
      ) : showGeneratedQR ? (
        // generated QR code image
        <Box
          as="img"
          src={qrCodeImage}
          draggable={false}
          width={72}
          height={72}
        />
      ) : (
        // blank QR code image
        <Box
          draggable={false}
          width={72}
          height={72}
        />
      )}
      <Box>
        {shouldHighlightLink ? (
          <Box
            fontWeight="bold"
            style={{ textDecoration: 'underline 0.5px #D9D9D9', textUnderlineOffset: '4px', display: 'inline' }}
            {...(!isInEditor ? { as: 'a', href: link, cursor: 'pointer', target: '_blank' } : {})}
          >
            {formattedLink}
          </Box>
        ) : (
          formattedLink
        )}
        {password ? `\nPassword: ${password}` : ''}
      </Box>
    </Flex>
  );
};
